import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";

import {
  asDatePreset,
  isDatePreset,
  SendDeletionDatePreset,
} from "@bitwarden/common/tools/models/send-deletion-date-preset";
import { SendApiService } from "@bitwarden/common/tools/send/services/send-api.service.abstraction";
import { AuthType } from "@bitwarden/common/tools/send/types/auth-type";
import { SendType } from "@bitwarden/common/tools/send/types/send-type";
import {
  AsyncActionsModule,
  ButtonModule,
  CardComponent,
  CheckboxModule,
  CopyClickDirective,
  DialogService,
  FormFieldModule,
  IconButtonModule,
  Option,
  RadioButtonModule,
  SectionComponent,
  SectionHeaderComponent,
  SelectModule,
  ToastService,
  TypographyModule,
} from "@bitwarden/components";
import { SendDetailsComponent } from "@bitwarden/send-ui";
import { I18nPipe } from "@bitwarden/ui-common";

import { BvRemovePasswordDialogComponent } from "../shared/bv-remove-password-dialog.component";

import { BvSendFileDetailsComponent } from "./bv-send-file-details.component";
import { BvSendOptionsComponent } from "./bv-send-options.component";
import { BvSendTextDetailsComponent } from "./bv-send-text-details.component";
import { AuthTypeNamePipe } from "./pipes/auth-type-name.pipe";

const EXPIRATION_NEVER = "never";

@Component({
  selector: "app-bv-send-details",
  templateUrl: "./bv-send-details.component.html",
  imports: [
    CommonModule,
    SectionComponent,
    SectionHeaderComponent,
    TypographyModule,
    AuthTypeNamePipe,
    I18nPipe,
    CardComponent,
    FormFieldModule,
    ReactiveFormsModule,
    BvSendTextDetailsComponent,
    BvSendFileDetailsComponent,
    BvSendOptionsComponent,
    IconButtonModule,
    CheckboxModule,
    SelectModule,
    AsyncActionsModule,
    ButtonModule,
    RadioButtonModule,
    CopyClickDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BvSendDetailsComponent extends SendDetailsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly toastService = inject(ToastService);
  private readonly sendApiService = inject(SendApiService);

  readonly copyLinkOnSave = signal(false);
  protected readonly deactivateSend = signal(false);
  protected readonly optionsExpanded = signal(false);
  readonly sendOptionsComponent = viewChild(BvSendOptionsComponent);

  protected readonly expirationDatePresetOptions = signal<
    Option<SendDeletionDatePreset | string>[]
  >([]);
  protected readonly selectedExpirationDatePresetControl = new FormControl<
    SendDeletionDatePreset | string
  >(EXPIRATION_NEVER);

  protected toggleOptions(): void {
    this.optionsExpanded.update((expanded) => !expanded);
  }

  protected isSendType(type: SendType): boolean {
    return this.sendFormService.sendFormConfig.sendType === type;
  }

  protected onCopyLinkOnSaveChange(event: Event): void {
    this.copyLinkOnSave.set((event.target as HTMLInputElement).checked);
  }

  protected onDeactivateSendChange(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.deactivateSend.set(checked);
    this.sendFormService.patchSend((send) => Object.assign(send, { disabled: checked }));
  }

  protected onPasswordInput(): void {
    const password = this.sendDetailsForm.get("password")?.value;
    const hasPassword = password != null && password !== "" && password !== "************";
    const authTypeControl = this.sendDetailsForm.get("authType");
    if (!authTypeControl) {
      return;
    }
    if (hasPassword && authTypeControl.value !== AuthType.Email) {
      authTypeControl.setValue(AuthType.Password, { emitEvent: true });
    } else if (!hasPassword && authTypeControl.value === AuthType.Password) {
      authTypeControl.setValue(AuthType.None, { emitEvent: true });
    }
  }

  override async ngOnInit() {
    this.expirationDatePresetOptions.set([
      { label: this.i18nService.t("never"), value: EXPIRATION_NEVER },
      ...this.datePresetOptions,
    ]);

    this.selectedExpirationDatePresetControl.setValue(this.getInitialExpirationPreset());

    this.selectedExpirationDatePresetControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.patchExpirationDate());

    this.sendDetailsForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.patchExpirationDate());

    await super.ngOnInit();

    const updatedSendView = this.sendFormService.updatedSendView();
    if (updatedSendView?.expirationDate && this.sendFormService.sendFormConfig?.mode !== "add") {
      const expirationValue = updatedSendView.expirationDate.toString();
      const hasOption = this.expirationDatePresetOptions().some(
        (option) => option.value === expirationValue,
      );
      if (!hasOption) {
        this.expirationDatePresetOptions.update((options) => [
          {
            label: this.datePipe.transform(updatedSendView.expirationDate, "short"),
            value: expirationValue,
          },
          ...options,
        ]);
      }
      this.selectedExpirationDatePresetControl.setValue(expirationValue);
    }

    this.patchExpirationDate();
  }

  protected get expirationDateDisplay(): string {
    const expirationDate = this.sendFormService.originalSendView()?.expirationDate;
    if (!expirationDate) {
      return this.i18nService.t("never");
    }
    return this.datePipe.transform(expirationDate, "short") ?? "";
  }

  private getInitialExpirationPreset(): SendDeletionDatePreset | string {
    const expirationDate = this.sendFormService.updatedSendView()?.expirationDate;
    if (!expirationDate) {
      return EXPIRATION_NEVER;
    }
    if (this.sendFormService.sendFormConfig?.mode !== "add") {
      return expirationDate.toString();
    }
    return EXPIRATION_NEVER;
  }

  private getFormattedExpirationDate(): Date | null {
    const selectedValue = this.selectedExpirationDatePresetControl.value;
    if (selectedValue === EXPIRATION_NEVER || selectedValue == null) {
      return null;
    }
    if (typeof selectedValue === "string") {
      return new Date(selectedValue);
    }
    const preset = asDatePreset(selectedValue);
    if (!isDatePreset(preset)) {
      return null;
    }
    const now = new Date();
    const milliseconds = now.setTime(now.getTime() + preset * 60 * 60 * 1000);
    return new Date(milliseconds);
  }

  private patchExpirationDate(): void {
    this.sendFormService.patchSend((send) => {
      send.expirationDate = this.getFormattedExpirationDate();
      return send;
    });
  }

  override readonly removePassword = async () => {
    const originalSendViewId = this.sendFormService.originalSendView()?.id;
    if (!originalSendViewId) {
      return;
    }

    const confirmed = await BvRemovePasswordDialogComponent.open(this.dialogService);
    if (!confirmed) {
      return;
    }

    try {
      await this.sendApiService.removePassword(originalSendViewId);
    } catch {
      return;
    }

    this.toastService.showToast({
      variant: "success",
      title: null,
      message: this.i18nService.t("removedPassword"),
    });
    this.passwordRemoved = true;
    this.sendDetailsForm.patchValue({
      authType: AuthType.None,
      password: null,
    });
  };
}
